import { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import {
  formatDate,
  formatCurrency,
  parseBarcode,
  buildEffectivePriceMap,
} from '#common/utils';
import URLSearchParamsEx from '#utils/url-ex';
import UserShippingInfo from '#components/UserShippingInfo';
import UserAdditionalChanges from '#components/UserAdditionalChanges';
import UserOrderItems from '#components/UserOrderItems';
import {
  useGetInvoiceMutation,
} from '#services/invoice';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { INOVICE_ITEM_TYPES } from '#common/constants';
import type { AppConfigState } from '../app/config.slice';

interface InvoicePackage {
  barcodeItems?: string[];
  shipper?: string;
  [key: string]: unknown;
}

interface InvoiceItem {
  id: string | number;
  fromInventory?: boolean;
  barcodeItems?: Array<{ barcode: string }>;
  amounts: number;
  [key: string]: unknown;
}

interface AdditionalCharge {
  _id: string | number;
  price: number | string;
  [key: string]: unknown;
}

interface InvoiceLineItem {
  type: string | number;
  value: string;
  [key: string]: unknown;
}

interface InvoiceInner {
  items: InvoiceLineItem[];
  number?: string | number;
  invoiceDate?: string;
  total?: number;
  name?: string;
  [key: string]: unknown;
}

interface InvoiceUser {
  code?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  disableShippingInfo?: boolean;
  shippingName?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  shippingAddress?: string;
  disableBillingInfo?: boolean;
  billingName?: string;
  billingEmail?: string;
  billingPhone?: string;
  billingAddress?: string;
  [key: string]: unknown;
}

interface Invoice {
  packages?: InvoicePackage[];
  items?: InvoiceItem[];
  additionalCharges?: AdditionalCharge[];
  invoices: InvoiceInner;
  user: InvoiceUser;
  appliedCoupons?: unknown;
  [key: string]: unknown;
}

export default function InvoiceDetail() {
  const appConfig = useSelector((state: { appConfig: AppConfigState }) => state.appConfig);
  const location = useLocation();

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [getInvoice] = useGetInvoiceMutation();
  const [invoice, setInvoice] = useState<Invoice>();

  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  useEffect(() => {
    void getInvoice(urlParams.values)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        if (hasError) {
          return;
        }

        setInvoice(res.data as Invoice);
      });
  }, []);

  if (!invoice) {
    return null;
  }

  const packages = invoice.packages || [];
  const items = invoice.items || [];
  const additionalCharges = invoice.additionalCharges || [];

  const relatedShippingInfo = invoice.invoices.items.reduce<InvoicePackage[]>((pinfo, iitm) => {
    if (iitm.type !== INOVICE_ITEM_TYPES.BARCODE_ITEM) {
      return pinfo;
    }

    const shippingInfo = packages.find((pkg) => (
      Array.isArray(pkg.barcodeItems) && pkg.barcodeItems.includes(iitm.value)
    ));
    if (!shippingInfo) {
      return pinfo;
    }

    if (pinfo.includes(shippingInfo)) {
      return pinfo;
    }

    pinfo.push(shippingInfo);
    return pinfo;
  }, []);

  const shippedVia = relatedShippingInfo
    .map((r) => r.shipper)
    .filter(Boolean)
    .join('/') || 'N/A';

  const orderItemsProps = {
    rows: invoice.invoices.items.reduce<InvoiceItem[]>((orderItems, iitm) => {
      if (iitm.type !== INOVICE_ITEM_TYPES.BARCODE_ITEM) {
        return orderItems;
      }

      const parsed = parseBarcode(iitm.value);
      const item = items.find((it) => {
        const { fromInventory } = it;
        const itemBarcodes = it.barcodeItems && it.barcodeItems.length ? it.barcodeItems.map((bi) => bi.barcode) : [];
        if (fromInventory) {
          return itemBarcodes.some((b) => {
            const p = parseBarcode(b);
            return (p!.itemId === parsed!.itemId);
          });
        }

        return (it.id === parsed!.itemId);
      });

      if (item && !orderItems.includes(item)) {
        orderItems.push(item);
      }

      return orderItems;
    }, []),
  };

  const additionalChargesProps = {
    rows: additionalCharges.filter((iitm) => {
      return invoice.invoices.items.some((it) => it.value === iitm._id);
    }),
  };

  const shippingInfoProps = {
    rows: relatedShippingInfo,
  };

  const effectivePriceMap = buildEffectivePriceMap(
    items as unknown as Parameters<typeof buildEffectivePriceMap>[0],
    invoice.appliedCoupons as Parameters<typeof buildEffectivePriceMap>[1],
  );

  const subtotal = invoice.invoices.items.reduce((total, itm) => {
    const { type, value } = itm;
    if (type === INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE) {
      const find = additionalCharges.find((ac) => ac._id === value);
      if (find) {
        total += Number(find.price);
      }
    } else if (type === INOVICE_ITEM_TYPES.BARCODE_ITEM) {
      const barcode = parseBarcode(value);
      const find = items.find((itm2) => {
        const { fromInventory } = itm2;
        const itemBarcodes = itm2.barcodeItems && itm2.barcodeItems.length ? itm2.barcodeItems.map((bi) => bi.barcode) : [];
        if (fromInventory) {
          return itemBarcodes.some((b) => {
            const p = parseBarcode(b);
            return (p!.itemId === barcode!.itemId);
          });
        }

        return (itm2.id === barcode!.itemId);
      });
      if (find) {
        const effectiveTotal = effectivePriceMap[find.id] || 0;
        const effectiveUnitPrice = find.amounts > 0 ? effectiveTotal / find.amounts : 0;
        total += effectiveUnitPrice;
      }
    }
    return total;
  }, 0);

  return (
    <>
      <Paper
        sx={{
          px: 2,
          mt: 2,
          '& .MuiGrid-root': { mb: 2 },
        }}
      >
        <Box sx={{ typography: 'subtitle2' }}>
          <Grid container>
            <Grid size={4}>
              Invoice Number
            </Grid>
            <Grid size={8}>
              {invoice.invoices.number}
            </Grid>

            <Grid size={4}>
              Invoice Date
            </Grid>
            <Grid size={8}>
              {formatDate(invoice.invoices.invoiceDate as string)}
            </Grid>

            <Grid size={4}>
              Invoice Amount
            </Grid>
            <Grid size={8}>
              {formatCurrency(invoice.invoices.total as number)}
            </Grid>

            <Grid size={4}>
              Created By
            </Grid>
            <Grid size={8}>
              {invoice.invoices.name}
            </Grid>

            <Grid size={4}>
              Shipped Via
            </Grid>
            <Grid size={8}>
              {shippedVia}
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              Account #
            </Grid>
            <Grid size={8}>
              {invoice.user.code}
            </Grid>

            <Grid size={4}>
              Contact Info
            </Grid>
            <Grid size={8}>
              <Box>{invoice.user.name}</Box>
              <Box>{invoice.user.email}</Box>
              <Box>{invoice.user.phone}</Box>
              <Box>{invoice.user.address}</Box>
            </Grid>

            {
              !invoice.user.disableShippingInfo && (
                <>
                  <Grid size={4}>
                    Shipping Info
                  </Grid>
                  <Grid size={8}>
                    <Box>{invoice.user.shippingName}</Box>
                    <Box>{invoice.user.shippingEmail}</Box>
                    <Box>{invoice.user.shippingPhone}</Box>
                    <Box>{invoice.user.shippingAddress}</Box>
                  </Grid>
                </>
              )
            }

            {
              !invoice.user.disableBillingInfo && (
                <>
                  <Grid size={4}>
                    Billing Info
                  </Grid>
                  <Grid size={8}>
                    <Box>{invoice.user.billingName}</Box>
                    <Box>{invoice.user.billingEmail}</Box>
                    <Box>{invoice.user.billingPhone}</Box>
                    <Box>{invoice.user.billingAddress}</Box>
                  </Grid>
                </>
              )
            }
          </Grid>
        </Box>
      </Paper>
      <UserOrderItems
        orderItems={orderItemsProps}
        order={invoice as unknown as React.ComponentProps<typeof UserOrderItems>['order']}
        cancelled={false}
        isInvoice
      />
      <UserAdditionalChanges
        additionalCharges={additionalChargesProps}
      />
      <UserShippingInfo
        shippingInfo={shippingInfoProps}
        isInvoice
      />
      <Box
        sx={{
          typography: 'subtitle1',
          display: 'flex',
          justifyContent: 'flex-end',
          pt: 2,
        }}
      >
        <b>Total</b>
        :
        {' '}
        {formatCurrency(invoice.invoices.total ?? subtotal)}
      </Box>
      <Divider />
      <Box
        id="invoice-footer"
        sx={{
          mt: 2,
          typography: 'subtitle2',
        }}
      >
        Thank you for choosing&nbsp;
        {appConfig.wholesaleName}
        !
        <br />
        Best regards,
        <br />
        The&nbsp;
        {appConfig.wholesaleName}
        {' '}
        &nbsp;
        Customer Service Team |
        &nbsp;
        {appConfig.wholesaleEmail}
        <br />
        <br />
        This order is subject to company TERMS & CONDITIONS.
      </Box>
    </>
  );
}
